
    import Vue from "vue";
    import Component from "vue-class-component";
    import { StateChanger } from "vue-infinite-loading";
    import { Watch } from "vue-property-decorator";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import PrimaryButton from "@/vue/components/PrimaryButton.vue";
    import DatePicker from "@/vue/components/DatePicker.vue";
    import VisitDialogue from "@/vue/components/VisitDialogue.vue";
    import { VisitSearchParameters } from "@/model/VisitSearchParameters";
    import { IVisitSearchResult, VisitSearchResult } from "@/model/VisitSearchResult";
    import { ILookupItem } from "@/model/LookupItem";
    import { ISiteLookup } from "@/model/Site";
    import { UserRole, VisitStatus } from "@/model/Enums";
    import { IVisitSearchResponse } from "@/model/VisitSearchResponse";
    import { IClientLookup } from "@/model/Client";

    @Component({
        components: { ApiButton, PrimaryButton, DatePicker, VisitDialogue }
    })
    export default class Visits extends Vue {

        async mounted(): Promise<void> {

            // fire off all the loads in parallel
            const loadCompanies = async () => { this.visitorCompanyList = await ApiClient.get("/api/visitorCompany/lookups"); }
            const loadRecipients = async () => { this.recipientList = await ApiClient.get(`/api/siteRecipient/lookups?siteID=${Utils.emptyGuidValue}`); }
            const loadClients = async () => { this.clientList = await ApiClient.get(Utils.addClientQueryParameters("/api/client/lookups")); }
            const loadSites = async () => { this.siteList = await ApiClient.get(Utils.addClientQueryParameters("/api/site/lookups")); }
            await Promise.all([
                loadCompanies(),
                loadRecipients(),
                loadClients(),
                loadSites()
            ]);

            // manually set description for some items in statusList (hardly seems worth using the enum!)
            const setDescription = (status: VisitStatus, description: string) => {
                const item = this.statusList.find(s => s.id === status);
                if(item) item.description = description;
            };
            setDescription(VisitStatus.None, "Any");
            setDescription(VisitStatus.Registered, "Registered");
            setDescription(VisitStatus.BriefingStarted, "Briefing Started");
            setDescription(VisitStatus.TestStarted, "Test Started");
            setDescription(VisitStatus.CompletedAndPassed, "Completed and Passed");
        }

        //
        // -- properties
        //

        infiniteId: number = + new Date();
        searchParameters = new VisitSearchParameters();
        get canFilterClient(): boolean { return this.$store.state.signedInUser?.canFilterClient === true; }
        get canFilterSite(): boolean { return this.$store.state.signedInUser?.canFilterSite === true; }
        clientList: Array<IClientLookup> = [];
        resultCount: string = "-";
        get clientOptions(): Array<IClientLookup> { return Utils.clientOptionsFilter(this.clientList); }
        siteList: Array<ISiteLookup> = [];
        get siteOptions(): Array<ISiteLookup> { return Utils.siteOptionsFilter(this.siteList, this.searchParameters.clientID); }
        get statusList(): Array<ILookupItem> { return Utils.enumToLookups(VisitStatus); } // see also mounted()
        visitorCompanyList: Array<ILookupItem> = [];
        recipientList: Array<ILookupItem> = [];
        visitList: Array<VisitSearchResult> = [];

        //
        // -- Watchers
        //

        debouncedRefreshSearch = Utils.debounce(this.refreshSearch, 400);

        @Watch("searchParameters.searchText")
        onSearchTextChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.shouldSearchExtraFields")
        onShouldSearchExtraFieldsChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.clientID")
        onClientIdChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.siteID")
        onSiteIdChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.status")
        onStatusChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.isLatestVisitPerEmail")
        onIsLatestVisitPerEmailChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.createdFrom")
        onCreatedFromChanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.createdTo")
        onCreatedToChanged(): void {
            this.debouncedRefreshSearch();
        }

        //
        // -- methods
        //

        shouldShowResult(searchResult: VisitSearchResult): boolean {
            return searchResult.visit.answerCount > 0 && Utils.hasDateValue(searchResult.visit.completed);
        }

        visitImageSource(result: VisitSearchResult): string {
            return Utils.isEmptyId(result?.visit?.photoResource?.id)
                ? result?.siteImage?.sourceUrl ?? require("@/images/missing.png")
                : result?.visit?.photoResource?.sourceUrl ?? require("@/images/missing.png");
        }

        resetParameters() {
            this.searchParameters.reset();
        }

        async downloadCsv(event: Event): Promise<void> {
            const blob = await ApiClient.post("api/visit/csvDownload", this.searchParameters) as Blob;
            const blobUrl = window.URL.createObjectURL(blob);

            // at this point we _could_ just do:
            //    window.location.assign(blobUrl);
            // ...but the hackery below is required if we want a sensible file name (*sigh*)
            const link = document.createElement("a");
            link.href = blobUrl;
            link.target = "_blank";
            link.download = `visits-${Utils.timeStampText()}.csv`; // 'download' is an HTML5 attribute
            link.style.display = "none";
            link.click();
            setTimeout(() => {
                window.URL.revokeObjectURL(blobUrl);
            }, 10);
        }

        viewDetail(result: IVisitSearchResult): void {
            const dialog: VisitDialogue = this.$refs.visitDialogue as VisitDialogue;
            dialog.show(result);
        }

        refreshSearch(): void {
            this.resultCount = "-";
            this.visitList = [];
            this.searchParameters.pageNumber = 1;
            this.infiniteId += 1;
        }

        async infiniteLoadingHandler(stateChanger: StateChanger): Promise<void> {
            const response: IVisitSearchResponse = await ApiClient.post("/Api/Visit/Search", this.searchParameters);
            if(this.searchParameters.pageNumber === 1) {
                this.resultCount = String(response.count);
            }
            if (response.list.length) {
                this.visitList.push(...response.list.map((v: IVisitSearchResult) => new VisitSearchResult(v)));
                this.searchParameters.pageNumber += 1;
                stateChanger.loaded();
            }
            else {
                stateChanger.complete();
            }
        }
    }
